import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Building2, Menu, X, ChevronDown, Download } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, profile } = useAuth();
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();

  const navItems = [
    { name: "Features", href: "#benefits" },
    { name: "Pricing", href: "#pricing" },
    { name: "FAQ", href: "#faq" },
  ];

  // Dynamic logo destination based on auth state
  const getLogoDestination = () => {
    if (user && profile) {
      return profile.role === 'landlord' ? '/dashboard' : '/tenant-portal/dashboard';
    }
    return '/';
  };

  return (
    <header className="sticky top-0 z-50 bg-card/90 backdrop-blur-sm border-b border-border">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to={getLogoDestination()} className="flex items-center space-x-2 hover:opacity-80 transition-opacity cursor-pointer">
            <Building2 className="h-8 w-8 text-primary" />
            <span className="text-2xl font-bold text-foreground">RentFlow</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="text-muted-foreground hover:text-primary font-medium transition-colors"
              >
                {item.name}
              </a>
            ))}
          </nav>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Install Button */}
            {!isInstalled && (
              isInstallable ? (
                <Button variant="ghost" size="sm" onClick={promptInstall} className="gap-2">
                  <Download className="h-4 w-4" />
                  Install App
                </Button>
              ) : (
                <Link to="/install">
                  <Button variant="ghost" size="sm" className="gap-2">
                    <Download className="h-4 w-4" />
                    Install App
                  </Button>
                </Link>
              )
            )}
            
            {/* Login Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-1">
                  Login
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/login" className="w-full cursor-pointer">
                    Landlord Login
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/tenant-portal/login" className="w-full cursor-pointer">
                    Tenant Login
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Link to="/signup">
              <Button>
                Sign Up
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-border">
            <div className="flex flex-col space-y-6 pt-4">
              {/* Navigation Items */}
              <div className="space-y-4">
                {navItems.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className="block text-muted-foreground hover:text-primary font-medium py-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </a>
                ))}
              </div>
              
              {/* Login Section */}
              <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                <div className="text-sm font-semibold text-foreground mb-3">Login Options</div>
                <Link 
                  to="/login" 
                  className="block w-full text-left p-3 rounded-md bg-background hover:bg-muted text-foreground hover:text-primary font-medium transition-colors border border-border"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Landlord Login
                </Link>
                <Link 
                  to="/tenant-portal/login" 
                  className="block w-full text-left p-3 rounded-md bg-background hover:bg-muted text-foreground hover:text-primary font-medium transition-colors border border-border"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Tenant Login
                </Link>
              </div>
              
              {/* Install App Button */}
              {!isInstalled && (
                <div className="pt-2">
                  {isInstallable ? (
                    <Button 
                      variant="outline" 
                      className="w-full gap-2" 
                      onClick={() => {
                        promptInstall();
                        setIsMenuOpen(false);
                      }}
                    >
                      <Download className="h-4 w-4" />
                      Install App
                    </Button>
                  ) : (
                    <Link to="/install" onClick={() => setIsMenuOpen(false)}>
                      <Button variant="outline" className="w-full gap-2">
                        <Download className="h-4 w-4" />
                        Install App
                      </Button>
                    </Link>
                  )}
                </div>
              )}
              
              {/* Sign Up Button */}
              <Link to="/signup" onClick={() => setIsMenuOpen(false)}>
                <Button className="w-full">
                  Sign Up
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
